import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useCanAccess } from './useCanAccess';
import type { RoleLevel } from '@/plugins/types';

interface RoleGateProps {
  children: ReactNode;
  pluginId?: string;
  minOrgRole?: RoleLevel;
  minWorkspaceRole?: RoleLevel;
  fallbackPath?: string;
  loadingElement?: ReactNode;
  deniedElement?: ReactNode;
}

const DEFAULT_FALLBACK = '/app/dashboard';

export function RoleGate({
  children,
  pluginId,
  minOrgRole,
  minWorkspaceRole,
  fallbackPath = DEFAULT_FALLBACK,
  loadingElement,
  deniedElement,
}: RoleGateProps) {
  const { checkPlugin, hasMinOrgRole, hasMinWorkspaceRole, isLoading } = useCanAccess();

  if (isLoading) {
    return <>{loadingElement ?? <RoleGateLoadingSkeleton />}</>;
  }

  if (pluginId) {
    const { canAccess } = checkPlugin(pluginId);
    if (!canAccess) {
      return deniedElement ? <>{deniedElement}</> : <Navigate to={fallbackPath} replace />;
    }
  }

  if (minOrgRole && !hasMinOrgRole(minOrgRole)) {
    return deniedElement ? <>{deniedElement}</> : <Navigate to={fallbackPath} replace />;
  }

  if (minWorkspaceRole && !hasMinWorkspaceRole(minWorkspaceRole)) {
    return deniedElement ? <>{deniedElement}</> : <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

function RoleGateLoadingSkeleton() {
  return (
    <div className="flex items-center justify-center h-32" data-testid="role-gate-loading">
      <div className="animate-pulse flex flex-col items-center gap-2">
        <div className="h-4 w-32 bg-muted rounded" />
        <div className="h-3 w-24 bg-muted/60 rounded" />
      </div>
    </div>
  );
}
